/**
 * エラーコード定義
 * バックエンドと同期している必要があります
 */

export const ErrorCodes = {
  // Stripeエラー (1xxx)
  STRIPE_CONNECTION_ERROR: 1001,
  STRIPE_API_KEY_ERROR: 1002,
  STRIPE_TIMEOUT_ERROR: 1003,
  STRIPE_CARD_DECLINED: 1101,
  STRIPE_CARD_EXPIRED: 1102,
  STRIPE_INVALID_CVC: 1103,
  STRIPE_INSUFFICIENT_FUNDS: 1104,
  STRIPE_CARD_NOT_SUPPORTED: 1105,
  STRIPE_INTERNAL_ERROR: 1201,
  STRIPE_RATE_LIMIT_ERROR: 1202,
  STRIPE_INVALID_REQUEST: 1203,
  STRIPE_CAPTURE_ERROR: 1204,
  
  // Printfulエラー (2xxx)
  PRINTFUL_CONNECTION_ERROR: 2001,
  PRINTFUL_API_KEY_ERROR: 2002,
  PRINTFUL_TIMEOUT_ERROR: 2003,
  PRINTFUL_OUT_OF_STOCK: 2101,
  PRINTFUL_INVALID_ADDRESS: 2102,
  PRINTFUL_PRODUCT_ERROR: 2103,
  PRINTFUL_POSITION_ERROR: 2104,
  PRINTFUL_VALIDATION_ERROR: 2105,
  
  // システムエラー (3xxx)
  DATABASE_ERROR: 3001,
  INTERNAL_ERROR: 3002,
  VALIDATION_ERROR: 3003,
} as const;

export type ErrorCode = typeof ErrorCodes[keyof typeof ErrorCodes];

export const ErrorTypes = {
  ADMIN_CONTACT: 'admin',  // 管理者問い合わせ必要
  USER_ACTION: 'user',      // ユーザー対応可能
} as const;

export type ErrorType = typeof ErrorTypes[keyof typeof ErrorTypes];

interface ErrorInfo {
  type: ErrorType;
  message: string;
  message_en?: string;
}

const ERROR_MESSAGES: Record<number, ErrorInfo> = {
  // Stripeエラー
  [ErrorCodes.STRIPE_CONNECTION_ERROR]: {
    type: 'admin',
    message: '決済サービスとの接続に失敗しました。管理者にお問い合わせください。',
    message_en: 'Failed to connect to payment service. Please contact administrator.'
  },
  [ErrorCodes.STRIPE_CARD_DECLINED]: {
    type: 'user',
    message: 'カードの与信に失敗しました。別のカードをお試しください。',
    message_en: 'Card was declined. Please try another card.'
  },
  [ErrorCodes.STRIPE_CARD_EXPIRED]: {
    type: 'user',
    message: 'カードの有効期限が切れています。',
    message_en: 'Card has expired.'
  },
  [ErrorCodes.STRIPE_INSUFFICIENT_FUNDS]: {
    type: 'user',
    message: 'カードの残高が不足しています。',
    message_en: 'Insufficient funds.'
  },
  [ErrorCodes.STRIPE_CAPTURE_ERROR]: {
    type: 'admin',
    message: '決済の確定に失敗しました。管理者にお問い合わせください。',
    message_en: 'Failed to capture payment. Please contact administrator.'
  },
  
  // Printfulエラー
  [ErrorCodes.PRINTFUL_CONNECTION_ERROR]: {
    type: 'admin',
    message: 'Printfulとの連携に失敗しました。管理者にお問い合わせください。',
    message_en: 'Failed to connect to Printful. Please contact administrator.'
  },
  [ErrorCodes.PRINTFUL_OUT_OF_STOCK]: {
    type: 'admin',
    message: '商品が在庫切れです。管理者にお問い合わせください。',
    message_en: 'Product is out of stock. Please contact administrator.'
  },
  [ErrorCodes.PRINTFUL_INVALID_ADDRESS]: {
    type: 'user',
    message: '配送先住所に問題があります。入力内容をご確認ください。',
    message_en: 'Invalid shipping address. Please check your input.'
  },
  [ErrorCodes.PRINTFUL_POSITION_ERROR]: {
    type: 'admin',
    message: 'デザインデータにエラーがあります。管理者にお問い合わせください。',
    message_en: 'Design data error. Please contact administrator.'
  },
  
  // システムエラー
  [ErrorCodes.INTERNAL_ERROR]: {
    type: 'admin',
    message: 'システムエラーが発生しました。管理者にお問い合わせください。',
    message_en: 'System error occurred. Please contact administrator.'
  },
};

/**
 * エラーコードから情報を取得
 */
export function getErrorInfo(errorCode: number, lang: 'ja' | 'en' = 'ja'): ErrorInfo & { code: number } {
  const info = ERROR_MESSAGES[errorCode];
  
  if (!info) {
    return {
      type: 'admin',
      message: `不明なエラーが発生しました。(コード: ${errorCode})`,
      message_en: `Unknown error occurred. (Code: ${errorCode})`,
      code: errorCode
    };
  }
  
  return {
    type: info.type,
    message: lang === 'ja' ? info.message : (info.message_en || info.message),
    code: errorCode
  };
}

/**
 * エラータイプが管理者問い合わせ必要かどうか
 */
export function isAdminContactRequired(errorCode: number): boolean {
  const info = getErrorInfo(errorCode);
  return info.type === 'admin';
}